/*
Briques de mise en page « éditoriale » partagées par les pages marketing
(homepage v5, Hall of Fame, Devenir hôte) : libellé de section numéroté,
constantes de surfaces, boutons et bandeau final sur photo.
*/
import Image from 'next/image';

/*
Rail de page : même gabarit que le contenu du header (headDesktop.js), pour que
les bords du contenu tombent exactement sous le logo et l'avatar.
*/
export const HEADER_RAIL = 'mx-auto w-full max-w-[1240px] px-5 lg:px-10';

export const SECTION = 'mx-auto max-w-[1240px] px-6 py-20 lg:px-8 lg:py-28';
export const CARD = 'rounded-xl border border-was-ink/8 bg-white';
export const CARD_HOVER = 'transition duration-300 ease-out hover:-translate-y-0.5 hover:border-was-ink/15';
export const BTN_PRIMARY = 'inline-flex items-center gap-2 rounded-lg bg-was-ink px-6 py-3 text-[15px] font-normal text-white transition-colors duration-200 hover:bg-was-violet hover:text-white';
export const BTN_SECONDARY = 'inline-flex items-center gap-2 rounded-lg border border-was-ink/15 px-6 py-3 text-[15px] font-normal text-was-ink transition-colors duration-200 hover:border-was-ink/40 hover:text-was-ink';
export const H2 = 'font-heading text-[26px] font-extrabold uppercase leading-[1.06] tracking-[-0.01em] text-was-ink md:text-[34px]';

export function Kicker({ n, scene = 'Scène', children }) {
  return (
    <div className="flex items-center gap-3 text-sm font-normal text-was-muted">
      {n && (
        <span className="tabular-nums">
          {scene}
          {' '}
          {n}
        </span>
      )}
      {n && <span className="h-px w-6 bg-was-ink/20" />}
      <span className="first-letter:uppercase">{children}</span>
    </div>
  );
}

export function ArrowIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

// Libellés courts des « Scène 0N » (les clés i18n existantes sont trop longues).
const KICKERS = {
  fr: {
    hof: ['Statuts & avantages', 'Vos avantages', 'Les trois statuts', 'Le détail qui compte'],
    behost: ['Proposer votre espace', 'Pourquoi accueillir', 'Comment ça se passe', 'Ils accueillent déjà', 'Questions fréquentes'],
    berider: ['Réserver un lieu', 'Trois étapes', 'Une recherche plus intelligente', 'Les étapes clés'],
    insurance: ['Assurance', 'Comment ça marche', 'Ce qui est couvert', 'Questions fréquentes', 'Aller plus loin'],
    sponsorship: ['Parrainage'],
    services: ['Nos services'],
    contact: ['Nous contacter'],
    press: ['Presse & média'],
    corpo: ['Corporate membership', 'Ce qui change', 'Comparatif', 'En pratique', 'Nos engagements', 'Rejoindre'],
    blog: ['Le blog'],
    legal: ['Informations légales'],
  },
  en: {
    hof: ['Status & benefits', 'Your benefits', 'The three tiers', 'The detail that matters'],
    behost: ['List your space', 'Why host', 'How it works', 'They already host', 'FAQ'],
    berider: ['Book a location', 'Three steps', 'Smarter search', 'Key steps'],
    insurance: ['Insurance', 'How it works', 'What is covered', 'FAQ', 'Go further'],
    sponsorship: ['Referral'],
    services: ['Our services'],
    contact: ['Contact us'],
    press: ['Press & media'],
    corpo: ['Corporate membership', 'What changes', 'Comparison', 'In practice', 'Our commitments', 'Join'],
    blog: ['The blog'],
    legal: ['Legal information'],
  },
  es: {
    hof: ['Estatus y ventajas', 'Tus ventajas', 'Los tres niveles', 'El detalle que cuenta'],
    behost: ['Proponer tu espacio', 'Por qué acoger', 'Cómo funciona', 'Ya son anfitriones', 'Preguntas frecuentes'],
    berider: ['Reservar un lugar', 'Tres pasos', 'Búsqueda más inteligente', 'Etapas clave'],
    insurance: ['Seguro', 'Cómo funciona', 'Qué está cubierto', 'Preguntas frecuentes', 'Ir más lejos'],
    sponsorship: ['Apadrinamiento'],
    services: ['Nuestros servicios'],
    contact: ['Contáctanos'],
    press: ['Prensa y medios'],
    corpo: ['Corporate membership', 'Qué cambia', 'Comparativa', 'En la práctica', 'Nuestros compromisos', 'Unirse'],
    blog: ['El blog'],
    legal: ['Información legal'],
  },
};

export function kicker(lang, page, index) {
  const dict = KICKERS[lang] || KICKERS.en;
  return (dict[page] || KICKERS.fr[page] || [])[index] || '';
}

export const H1 = 'font-heading text-[32px] font-extrabold uppercase leading-[1.04] tracking-[-0.01em] text-was-ink md:text-[40px] lg:text-[46px]';

// En-tête de page : Scène 01 + titre Unbounded + texte + actions, photo ou aside à droite.
export function PageHero({
 n = '01', label, title, children, image, imageAlt = '', aside, narrow = false,
}) {
  const hasSide = image || aside;
  return (
    <section className="bg-white">
      <div className={`mx-auto grid max-w-[1240px] items-center gap-10 px-6 pb-16 pt-28 lg:gap-16 lg:px-8 lg:pb-24 lg:pt-36 ${hasSide ? 'lg:grid-cols-[1.1fr_0.9fr]' : ''}`}>
        <div>
          <Kicker n={n}>{label}</Kicker>
          <h1 className={`mt-5 text-balance ${H1} ${narrow ? 'max-w-[640px]' : 'max-w-[820px]'}`}>{title}</h1>
          {children}
        </div>
        {image && (
          <div className={`relative aspect-[4/5] overflow-hidden ${CARD}`}>
            <Image src={image} alt={imageAlt} fill sizes="(max-width: 1024px) 100vw, 520px" className="object-cover" />
          </div>
        )}
        {aside}
      </div>
    </section>
  );
}

// Liste numérotée 01, 02, 03… à filets, deux colonnes sur desktop.
const LIST_COLUMNS = {
 1: '', 2: 'md:grid-cols-2 md:gap-x-16', 3: 'md:grid-cols-3 md:gap-x-12', 4: 'md:grid-cols-2 lg:grid-cols-4 md:gap-x-10',
};

export function NumberedList({ items, columns = 2 }) {
  return (
    <div className={`grid border-t border-was-ink/8 ${LIST_COLUMNS[columns] || ''}`}>
      {items.map((item, i) => (
        <div key={item.title || i} className="grid grid-cols-[48px_1fr] gap-4 border-b border-was-ink/8 py-8 lg:py-10">
          <span className="pt-1 text-sm tabular-nums text-was-muted">{String(i + 1).padStart(2, '0')}</span>
          <div className="flex min-w-0 flex-col gap-2">
            <h3 className="m-0 text-[20px] font-bold leading-[1.2] tracking-[-0.02em] text-was-ink">{item.title}</h3>
            <div className="text-[15px] leading-relaxed text-was-slate">{item.body}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Bandeau final : photo de plateau plein cadre, titre en capitales, boutons.
export function FinalBand({ title, children, image = '/img/home-v5/respiration-bts.jpg' }) {
  return (
    <section className="was-grain relative overflow-hidden bg-was-abyss">
      <div className="absolute inset-0">
        <Image src={image} alt="" fill sizes="100vw" className="object-cover object-center" />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(10,7,16,0.55)_0%,rgba(10,7,16,0.7)_60%,rgba(10,7,16,0.85)_100%)]" />
      <div className={`relative ${SECTION} lg:py-36`}>
        <h2 className="m-0 max-w-[900px] text-balance font-heading text-[28px] font-extrabold uppercase leading-[1.04] tracking-[-0.01em] text-white md:text-[44px]">{title}</h2>
        <div className="mt-8 flex flex-wrap gap-4">{children}</div>
      </div>
    </section>
  );
}
